import {Form, Formik} from 'formik'
import {useEffect, useState} from 'react'
import * as Yup from 'yup'

import InlineTable from 'src/feature/bite/component/InlineTable'
import {useIntl} from 'react-intl'
import {useStore} from 'src/feature/store/hook'
import {useUpdateStore} from '../../../context/useMutation'
import LocationOperationalDetails from './LocationOperationalDetails'
import {useGetDetail} from 'src/feature/store/context/useQuery'
import LocationContact from './LocationContact'
import {ConnectedFocusError} from 'focus-formik-error'

const LocationDetail = ({
  isDetail,
  setIsDetail,
  isCorporate,
}: {
  isDetail: {
    status: boolean
    id: string
  }
  setIsDetail: any
  isCorporate?: boolean
}) => {
  const intl = useIntl()

  const [editable, setEditable] = useState(false)
  const {isLoading: isLoadingDetails} = useGetDetail(isDetail?.id || '')
  const {detail, detailSet} = useStore()
  const {mutateAsync: updateStore, isLoading} = useUpdateStore({storeId: detail?.id || ''})

  const column = [
    [
      {
        title: 'Location ID',
        key: 'no',
        dataIndex: 'no',
      },
      {
        title: 'Location Name',
        key: 'name',
        dataIndex: 'name',
        edit: 'input',
        maxLength: 51,
        required: true,
        tooltip: 'Provide a name for the location to help differentiate it from others.',
      },
      {
        title: 'Apt/Building No',
        key: 'aptBuildingNo',
        dataIndex: 'aptBuildingNo',
        edit: 'input',
        maxLength: 51,
        required: true,
        tooltip: 'Specify the apartment or building number for accurate location details.',
      },
      {
        title: 'Address',
        key: 'addressLine',
        dataIndex: 'addressLine',
        edit: 'input',
        maxLength: 100,
        tooltip: 'Enter the full address of the location.',
        required: true,
      },
      {
        title: 'ZIP Code',
        key: 'zipCode',
        dataIndex: 'zipCode',
        edit: 'input',
        maxLength: 21,
        tooltip: 'Enter the postal code associated with this location.',
      },

      {
        title: 'Latitude',
        key: 'latitude',
        dataIndex: 'latitude',
        edit: 'input',
        maxLength: 51,
        required: true,
        tooltip: 'Enter the geographic latitude of the location for mapping accuracy.',
      },
    ],
    [
      {
        title: 'hidden',
        key: 'hidden',
        dataIndex: 'hidden',
        render: () => <h3 className=''> </h3>,
      },
      {
        title: intl.formatMessage({id: 'STORE.DETAIL.OVERVIEW.STATUS'}),
        key: 'status',
        dataIndex: 'status',
        status: true,
        edit: 'select',
        required: true,
        tooltip:
          'Select whether this location is Active or Inactive to manage its visibility on the platform.',
        valueSelect: true,
        selects: [
          // {value: 0, text: 'Passive'},
          {value: 1, text: 'Active'},
          {value: 2, text: 'Inactive'},
        ],
      },
      {
        title: 'Country',
        key: 'country',
        dataIndex: 'countryName',
        edit: 'country',
        formName: 'countryID',
        displayType: 'text',
        tooltip: 'Select the country where the location operates.',
        required: true,
      },
      {
        title: 'City',
        key: 'city',
        dataIndex: 'cityName',
        edit: 'city',
        formName: 'cityID',
        tooltip: 'Enter the city where the location is based.',
        required: true,
      },
      {
        title: 'Region',
        key: 'region',
        dataIndex: 'regionName',
        edit: 'region',
        formName: 'regionId',
        tooltip: 'Specify the region, state, or province where the location is situated.',
      },
      {
        title: 'Longitude',
        key: 'longitude',
        dataIndex: 'longitude',
        edit: 'input',
        tooltip: 'Enter the geographic longitude of the location for mapping accuracy.',
        maxLength: 51,
        required: true,
      },
    ],
  ]

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    aptBuildingNo: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(50, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 50})),
    addressLine: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),
    zipCode: Yup.string().max(
      10,
      intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 10})
    ),
    regionId: Yup.string(),
    longitude: Yup.string()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(-180, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -180}))
      .max(180, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_VALUE'}, {max: 180})),

    status: Yup.number().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    countryID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    cityID: Yup.string().required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'})),
    latitude: Yup.number()
      .required(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .typeError(intl.formatMessage({id: 'GLOBAL.VALIDATION.REQUIRED_FIELD'}))
      .min(-90, intl.formatMessage({id: 'GLOBAL.VALIDATION.MIN_VALUE'}, {min: -90}))
      .max(90, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_VALUE'}, {max: 90})),
  })

  if (isLoadingDetails) {
    return <div>Loading...</div>
  }

  return (
    <>
      {detail && !isLoadingDetails && (
        <Formik
          validationSchema={validationSchema}
          initialValues={{
            name: detail?.name || '',
            status: detail?.status || 1,
            addressLine: detail?.addressLine || '',
            aptBuildingNo: detail?.aptBuildingNo || '',
            zipCode: detail?.zipCode || '',
            countryID: detail?.countryID || 'f109e594-b301-ec11-a85d-000d3a229408',
            cityID: detail?.cityID || '',
            cityName: detail?.cityName || '',
            regionId: detail?.regionId || '',
            longitude: detail?.longitude || '',
            latitude: detail?.latitude || '',
            seasonPeriod: detail?.Optional?.seasonPeriod || false,
            seasonStartDate: detail?.Optional?.seasonStartDate || '',
            seasonEndDate: detail?.Optional?.seasonEndDate || '',
            workingDays: detail?.Optional?.workingDays || [],
            openingTime: detail?.Optional?.openingTime || '',
            closingTime: detail?.Optional?.closingTime || '',
            contactName: detail?.Contact?.firstname || '',
            contactSurname: detail?.Contact?.lastname || '',
            contactPhone: detail?.Contact?.phone || '',
            contactEmail: detail?.Contact?.email || '',
            contactTitle: detail?.Contact?.titleName || '',
          }}
          onSubmit={async (values, {resetForm}) => {
            const updateRes = (await updateStore({values})) as any

            if (!updateRes) {
              resetForm()
              return false
            }

            setEditable(false)
          }}
        >
          {({values, errors, touched, resetForm, handleSubmit}) => (
            <Form>
              <ConnectedFocusError />
              <button
                className='btn btn-primary mb-5 mb-xl-10'
                onClick={() => {
                  setEditable(false)
                  setIsDetail({status: false, type: 'detail', id: ''})
                }}
              >
                <i className='bi bi-arrow-left fs-2'></i>
                <span className='d-none d-md-inline'>
                  {intl.formatMessage({id: 'GLOBAL.BUTTON.BACK'})}
                </span>
              </button>
              <div className='card mb-5 mb-xl-10'>
                <div className='card-header'>
                  <div className='card-title m-0'>
                    <h3 className='card-label'>Location Info</h3>
                  </div>
                </div>
                <div className='card-body p-9'>
                  <InlineTable
                    loading={isLoading}
                    dataSource={detail}
                    columns={column}
                    editable={editable}
                    values={values}
                  />
                </div>
                <div className='card-footer d-flex w-100 justify-content-end'>
                  {editable ? (
                    <div>
                      <button
                        className='btn btn-primary'
                        style={{marginRight: 10}}
                        onClick={() => {
                          setEditable(false)
                          resetForm()
                        }}
                      >
                        <i className='bi bi-x me-2'></i>
                        {intl.formatMessage({id: 'GLOBAL.BUTTON.CANCEL'})}
                      </button>
                      <button className='btn btn-primary' disabled={isLoading} type='submit'>
                        <i className='bi bi-save2 me-2'></i>
                        {isLoading
                          ? intl.formatMessage({id: 'GLOBAL.BUTTON.SAVING'})
                          : intl.formatMessage({id: 'GLOBAL.BUTTON.SAVE'})}
                      </button>
                    </div>
                  ) : (
                    <button className='btn btn-primary' onClick={() => setEditable(true)}>
                      <i className='bi bi-pencil me-2'></i>
                      {intl.formatMessage({id: 'GLOBAL.BUTTON.EDIT'})}
                    </button>
                  )}
                </div>
              </div>

              {isCorporate && (
                <LocationOperationalDetails
                  loading={isLoading}
                  setFieldValue={detailSet}
                  detail={detail}
                  intl={intl}
                />
              )}
              {isCorporate && <LocationContact intl={intl} detail={detail} loading={isLoading} />}
            </Form>
          )}
        </Formik>
      )}
    </>
  )
}

export default LocationDetail
